import moment from 'moment';
import { useMemo, useState, useEffect } from 'react';
import { useMutation } from 'react-query';
import { toastr } from 'react-redux-toastr';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';
import HudVoucherService from '../../../services/hudVoucherService';
import useIntl from '../../../hooks/useIntl';
import messages from './messages';
import { submitVoucher } from '../../../services/tracsService';
import {
  DEFAULT_TRACS_VOUCHER_ID,
  LOCKED_VOUCHER_SUBMISSION_STATUSES,
} from '../../../constants/hudVoucher';

export function useSaveExecutionData({
  organizationId,
  propertyId,
  hudVoucherId,
  onSuccess,
}) {
  const hudVoucherService = useMemo(() => new HudVoucherService(), []);

  const mutation = useMutation(
    (payload) =>
      hudVoucherService.saveExecutionData(
        organizationId,
        propertyId,
        hudVoucherId,
        payload,
      ),
    {
      onSuccess: () => {
        toastr.success('Success', 'Execution data saved successfully.');
        if (onSuccess) {
          onSuccess();
        }
      },
      onError: (error) => {
        const message =
          error?.message ||
          error?.error ||
          (typeof error === 'string'
            ? error
            : 'Failed to save execution data. Please try again.');
        toastr.error('Error', message);
      },
    },
  );

  return mutation;
}

export function useReviewAndSubmitVoucherState({
  voucher,
  onStatusChange,
  onRefreshVoucher,
  onRefreshVoucherDetails,
  isReadOnly: initialReadOnly = false,
  hasUnsubmittedEligibleEvents = false,
  isVoucherEmpty = false,
  organizationId,
  propertyId,
  submissionBlockers = [],
}) {
  const { hudTracsIntegration } = useFlags();
  const [isReadOnly, setIsReadOnly] = useState(
    initialReadOnly || voucher?.submissionStatus === 'Submitted',
  );
  const intl = useIntl();

  useEffect(() => {
    setIsReadOnly(initialReadOnly || voucher?.submissionStatus === 'Submitted');
  }, [initialReadOnly, voucher?.submissionStatus]);

  const submissionStatus = voucher?.submissionStatus;
  const isVoucherDraft = submissionStatus === 'Draft';
  const isVoucherLocked =
    LOCKED_VOUCHER_SUBMISSION_STATUSES.includes(submissionStatus);
  /** Submitted / Paid: show “already submitted” tooltip; other non-Draft statuses get a generic message. */
  const showsAlreadySubmittedTooltip =
    submissionStatus === 'Submitted' || submissionStatus === 'Paid';

  const blockedByVoucherYearMonth =
    voucher?.submissionBlockedBy?.voucherYearMonth;
  const isBlockedByPrior = Boolean(
    voucher?.submissionStatus === 'Draft' && blockedByVoucherYearMonth,
  );
  const hasSubmissionBlockers =
    hasUnsubmittedEligibleEvents || submissionBlockers.length > 0;
  const hasValidationBlocker = submissionBlockers.includes(
    'REGULAR_ASSISTANCE_VALIDATION_OR_PAYABLE',
  );

  const savedExecutionData = useMemo(() => {
    if (!voucher?.executionSignerName) return null;
    return {
      name: voucher.executionSignerName,
      title: voucher.executionSignerTitle,
      voucherId: voucher.tracsVoucherId || DEFAULT_TRACS_VOUCHER_ID,
      streetAddress: voucher.executionStreetAddress,
      city: voucher.executionCity,
      state: voucher.executionState ? `US-${voucher.executionState}` : '',
      zipCode: voucher.executionZipCode,
      phoneNumber: voucher.executionPhoneNumber,
      date: voucher.executionDate ? moment(voucher.executionDate) : null,
    };
  }, [
    voucher?.executionSignerName,
    voucher?.executionSignerTitle,
    voucher?.tracsVoucherId,
    voucher?.executionStreetAddress,
    voucher?.executionCity,
    voucher?.executionState,
    voucher?.executionZipCode,
    voucher?.executionPhoneNumber,
    voucher?.executionDate,
  ]);

  const saveExecutionMutation = useSaveExecutionData({
    organizationId,
    propertyId,
    hudVoucherId: voucher?.id,
  });

  const handlePrepareExecution = async (data) => {
    const payload = {
      executionSignerName: data.name,
      executionSignerTitle: data.title,
      executionStreetAddress: data.streetAddress,
      executionCity: data.city,
      executionState: (data.state ?? '').replace('US-', ''),
      executionZipCode: String(data.zipCode ?? ''),
      executionPhoneNumber: data.phoneNumber,
      executionDate: data.date ? formatDateDB(data.date) : data.date,
    };

    if (data.voucherId) {
      payload.tracsVoucherId = data.voucherId;
    }

    await saveExecutionMutation.mutateAsync(payload);
    if (onRefreshVoucherDetails) {
      onRefreshVoucherDetails();
    }
  };

  const submitVoucherMutation = useMutation(
    async ({ wassId, password }) => {
      const voucherId = voucher?.id;

      if (!voucherId) {
        throw new Error('Voucher ID is missing');
      }

      return submitVoucher(voucherId, {
        username: wassId,
        password,
      });
    },
    {
      onSuccess: () => {
        setIsReadOnly(true);
        if (onStatusChange) {
          onStatusChange('Submitted');
        }
        if (onRefreshVoucher) {
          onRefreshVoucher();
        }
        toastr.success(
          intl.formatMessage(messages.toastrSuccessTitle),
          intl.formatMessage(messages.toastrSuccessBody),
        );
      },
      onError: (err) => {
        const message =
          err?.error ||
          err?.message ||
          err?.networkError ||
          (typeof err === 'string'
            ? err
            : intl.formatMessage(messages.toastrErrorBody));
        toastr.error(intl.formatMessage(messages.toastrErrorTitle), message);
        if (onRefreshVoucher) {
          onRefreshVoucher();
        }
      },
    },
  );

  const nonDraftTracsTooltip = useMemo(() => {
    if (showsAlreadySubmittedTooltip) {
      return intl.formatMessage(messages.alreadySubmittedToTracsTooltip);
    }
    return intl.formatMessage(messages.tracsActionsUnavailableNonDraftTooltip);
  }, [showsAlreadySubmittedTooltip, intl]);

  const submitToTracsDisabledTooltip = useMemo(() => {
    if (isVoucherEmpty) {
      return intl.formatMessage(messages.voucherHasNoDataTooltip);
    }
    if (hasValidationBlocker) {
      return intl.formatMessage(messages.validationBlockingTooltip);
    }
    if (!isVoucherDraft) {
      return nonDraftTracsTooltip;
    }
    if (isBlockedByPrior) {
      return intl.formatMessage(messages.priorVoucherMustBeApprovedTooltip, {
        voucherYearMonth: blockedByVoucherYearMonth ?? '',
      });
    }
    if (voucher?.hasDraftChangesSinceSubmission) {
      return intl.formatMessage(
        messages.draftChangesRequireResubmissionTooltip,
      );
    }
    return intl.formatMessage(messages.modalButtonTooltip);
  }, [
    isVoucherEmpty,
    hasValidationBlocker,
    isVoucherDraft,
    nonDraftTracsTooltip,
    isBlockedByPrior,
    blockedByVoucherYearMonth,
    intl,
    voucher?.hasDraftChangesSinceSubmission,
  ]);

  const handleSubmitToTracs = (wassId, password) =>
    submitVoucherMutation.mutateAsync({ wassId, password });

  return {
    handlePrepareExecution,
    handleSubmitToTracs,
    hasSubmissionBlockers,
    hasUnsubmittedEligibleEvents,
    hasValidationBlocker,
    hudTracsIntegration,
    isBlockedByPrior,
    isReadOnly,
    isVoucherDraft,
    isVoucherEmpty,
    isVoucherLocked,
    nonDraftTracsTooltip,
    saveExecutionLoading: saveExecutionMutation.isLoading,
    savedExecutionData,
    submitToTracsDisabledTooltip,
    submitVoucherMutation,
  };
}
